import { useState } from 'react';
import { Button, RadioButtonGroup, RadioButton } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { SidePanel } from '@carbon/ibm-products';
import { useAtomValue } from 'jotai';
import { CompletionConfigurationResponse } from 'types/api-responses';
import ErrorToast, { StandingErrorToast } from 'components/error-toast';
import { errorBodyMessage } from 'utils/api-errors';
import { useConfigurationOptionsAtom } from './atoms';
import {
  getPromptsForDocumentType,
  getDefaultPrompt,
  extensionsFromMimeTypes,
} from './utils';
import { useDocumentUpload } from './use-document-upload';
import { ConfigureProvisionFields } from './provision-fields';
import { DocumentUploadField } from './document-upload-field';
import styles from './add-document-side-panel.module.scss';

const extensionsForType = (
  formats: string[] | undefined,
  fallback: string[]
): string[] => {
  if (!formats?.length) return fallback;
  const exts = extensionsFromMimeTypes(formats);
  return exts.length ? exts : fallback;
};

// Per-document-type configuration supplied by each render site.
export interface DocumentUploadConfig {
  documentType: string;
  promptCategory: string;
  accept: string[];
  uploadHelperText: string;
  promptHelperText: string;
  idPrefix: string;
  errorFallback: string;
}

interface AddDocumentSidePanelProps {
  config: DocumentUploadConfig;
  title: string;
  subtitle: string;
  // Self-triggered when `triggerLabel` is set; controlled when `open` is passed.
  triggerLabel?: string;
  open?: boolean;
  onClose?: () => void;
  // Renders a Document Type radio group and derives type/prompt/formats from the
  // selection (instead of the fixed `config`). Used by Map Query.
  showDocumentTypeSelector?: boolean;
}

export const AddDocumentSidePanel = (props: AddDocumentSidePanelProps) => {
  const { triggerLabel, open } = props;
  const isControlled = open !== undefined;
  const configurationOptionsAtom =
    useConfigurationOptionsAtom('/configurations');
  const configuration = useAtomValue(configurationOptionsAtom);
  const configurationObject = configuration.data;
  const [configErrorDismissed, setConfigErrorDismissed] = useState(false);
  const ready = !!configurationObject && !('error' in configurationObject);

  if (!ready) {
    // While config is still loading, a controlled panel renders nothing (its
    // trigger lives on the page) and a self-triggered panel shows its CTA
    // disabled. A config-load error is surfaced in both modes so the path never
    // becomes a silent dead click.
    const configError =
      configurationObject && 'error' in configurationObject
        ? ((configurationObject as { error?: string }).error ??
          'Failed to load upload configuration')
        : null;
    return (
      <>
        {configError && !configErrorDismissed && (
          <ErrorToast
            key={configError}
            message={configError}
            onClose={() => {
              setConfigErrorDismissed(true);
              return true;
            }}
          />
        )}
        {!isControlled && (
          <Button size="sm" renderIcon={Add} disabled>
            {triggerLabel}
          </Button>
        )}
      </>
    );
  }

  return (
    <AddDocumentSidePanelInner
      configurationObject={
        configurationObject as CompletionConfigurationResponse
      }
      {...props}
    />
  );
};

const AddDocumentSidePanelInner = ({
  configurationObject,
  config,
  title,
  subtitle,
  triggerLabel,
  open,
  onClose,
  showDocumentTypeSelector,
}: AddDocumentSidePanelProps & {
  configurationObject: CompletionConfigurationResponse;
}) => {
  const isControlled = open !== undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState(config.documentType);

  // In selector mode the chosen type drives prompt category, accepted formats
  // and helper text; otherwise the fixed `config` values are used.
  const documentTypes = [...(configurationObject.documentTypes ?? [])].sort(
    (a, b) => a.label.localeCompare(b.label)
  );
  const typeOption = documentTypes.find((dt) => dt.value === selectedType);
  const documentType = showDocumentTypeSelector
    ? selectedType
    : config.documentType;
  const promptCategory =
    showDocumentTypeSelector && typeOption
      ? typeOption.promptCategory
      : config.promptCategory;
  const accept = showDocumentTypeSelector
    ? extensionsForType(typeOption?.acceptedFormats, config.accept)
    : config.accept;
  const promptHelperText =
    showDocumentTypeSelector && typeOption
      ? `For ${
          typeOption.label === typeOption.label.toUpperCase()
            ? typeOption.label
            : typeOption.label.toLowerCase()
        } documents`
      : config.promptHelperText;

  const filteredPrompts = getPromptsForDocumentType(
    configurationObject.promptConfigurations,
    promptCategory
  );

  const {
    upload,
    setUpload,
    ocrModel,
    setOcrModel,
    llmModel,
    setLlmModel,
    promptVersion,
    setPromptVersion,
    isSubmitting,
    isUploading,
    isUploadComplete,
    submitDisabled,
    handleFileUpload,
    handleSubmit,
    reset,
  } = useDocumentUpload({
    configurationObject,
    documentType,
    filteredPrompts,
    onSuccess: (data) => {
      window.location.href = data.href;
    },
    onError: (error) => {
      setSubmitError(errorBodyMessage(error, config.errorFallback));
    },
  });

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    const opt = documentTypes.find((dt) => dt.value === value);
    const prompts = getPromptsForDocumentType(
      configurationObject.promptConfigurations,
      opt?.promptCategory ?? ''
    );
    setPromptVersion(getDefaultPrompt(prompts));
  };

  const openState = isControlled ? !!open : isOpen;

  const submit = () => {
    setSubmitError(null);
    handleSubmit();
  };

  const handleClose = () => {
    if (upload.status !== 'uploading' && !isSubmitting) {
      if (isControlled) onClose?.();
      else setIsOpen(false);
      reset();
      setSubmitError(null);
    }
  };

  return (
    <>
      {submitError && (
        <StandingErrorToast
          message={submitError}
          onClose={() => setSubmitError(null)}
        />
      )}
      {!isControlled && triggerLabel && (
        <Button size="sm" renderIcon={Add} onClick={() => setIsOpen(true)}>
          {triggerLabel}
        </Button>
      )}
      <SidePanel
        open={openState}
        onRequestClose={handleClose}
        title={title}
        subtitle={subtitle}
        placement="right"
        size="md"
        slideIn
        selectorPageContent="#main-content"
        actions={[
          {
            kind: 'primary' as const,
            label: isSubmitting ? 'Adding...' : 'Add',
            disabled: submitDisabled,
            onClick: submit,
          },
          {
            kind: 'secondary' as const,
            label: 'Cancel',
            onClick: handleClose,
          },
        ]}
      >
        <div className={styles.body}>
          {showDocumentTypeSelector && documentTypes.length > 0 && (
            <div className={styles.section}>
              <RadioButtonGroup
                legendText="Document Type"
                name={`${config.idPrefix}document-type`}
                orientation="vertical"
                valueSelected={selectedType}
                onChange={(value) => handleTypeChange(value as string)}
                disabled={isSubmitting}
              >
                {documentTypes.map((opt) => (
                  <RadioButton
                    key={opt.value}
                    id={`${config.idPrefix}type-${opt.value}`}
                    value={opt.value}
                    labelText={opt.label}
                  />
                ))}
              </RadioButtonGroup>
            </div>
          )}

          <div className={styles.section}>
            <DocumentUploadField
              helperText={config.uploadHelperText}
              labelText="Drag and drop a file here or click to upload"
              accept={accept}
              upload={upload}
              onAddFiles={handleFileUpload}
              onDelete={() => !isSubmitting && setUpload(undefined)}
              disabled={isUploading || isSubmitting || isUploadComplete}
            />
          </div>

          <div className={styles.section}>
            <ConfigureProvisionFields
              idPrefix={config.idPrefix}
              extractionConfigurations={
                configurationObject.extractionConfigurations
              }
              completionConfigurations={
                configurationObject.completionConfigurations
              }
              filteredPrompts={filteredPrompts}
              ocrModel={ocrModel}
              onOcrChange={setOcrModel}
              llmModel={llmModel}
              onLlmChange={setLlmModel}
              promptVersion={promptVersion}
              onPromptChange={setPromptVersion}
              promptHelperText={promptHelperText}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </SidePanel>
    </>
  );
};
